import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { DateTime } from "luxon";

import { indoorClimateColumns } from "@utilifeed/config/consumption";
import { useBrandingContext, useGraphColors } from "@core";
import { Sensor } from "@core/apis";

import { BaseTimeSeriesChart, TimeSeriesData, YAxisConfig } from "../charts/BaseTimeSeriesChart";
import type { DateRange, Resolution } from "../shared/types";
import { heatRangeColors } from "../theme-constants";
import {
  FlexstationTemperatureBound,
  IndoorClimateSeriesType,
} from "./IndoorClimateChartContainer.types";
import { IndoorClimateChartLegend } from "./IndoorClimateChartLegend";
import { AVERAGE_SENSOR_UID } from "./SensorOverview";

// Types for the indoor climate chart
type TransformedData = {
  ts: number;
  humid: number;
  temp: number;
};

type TransformedSensor = Omit<Sensor, "data"> & {
  data: TransformedData[];
};

type IndoorClimateChartContainerProps = {
  sensors: Sensor[];
  selectedSensors: string[];
  outdoorSeries?: { x: number; y: number }[];
  flexstationTemperatureBounds?: FlexstationTemperatureBound[];
  visibleSeries: Record<string, boolean>;
  onSeriesToggle?: (seriesUid: string) => void;
  dateRange?: DateRange;
  resolution?: Resolution;
  isLoading?: boolean;
  chartHeight?: number;
  targetTemperature?: number | null;
};

const TEMP_AXIS_SPAN = 8;

// Transform sensor readings to chart format
function transformSensorsData(sensors: any[]): TransformedData[] {
  return sensors.map((item) => ({
    ts: DateTime.fromISO(item.timestamp).toMillis(),
    humid: item.values?.indoor_relative_humidity,
    temp: item.values?.living_air_temperature,
  }));
}

// Calculate axis bounds for temperature data
function calculateTemperatureAxisBounds(temperatureValues: number[]): {
  min: number;
  max: number;
} {
  const validTempValues = temperatureValues.filter((temp) => temp != null && !Number.isNaN(temp));

  if (validTempValues.length === 0) {
    return { min: 15, max: 25 };
  }

  const minTemp = Math.min(...validTempValues);
  const maxTemp = Math.max(...validTempValues);
  const dataRange = maxTemp - minTemp;
  const center = (minTemp + maxTemp) / 2;

  if (dataRange > TEMP_AXIS_SPAN) {
    const padding = dataRange * 0.1;
    return {
      min: minTemp - padding,
      max: maxTemp + padding,
    };
  }

  return {
    min: center - TEMP_AXIS_SPAN / 2,
    max: center + TEMP_AXIS_SPAN / 2,
  };
}

// Get column suffix for a given key
function getColumnSuffix(key: "temp" | "humid"): string {
  const column = indoorClimateColumns.find((col) => col.key === key);
  return column?.suffix ?? (key === "temp" ? "°C" : "%");
}

/**
 * IndoorClimateChartContainer - Container for indoor climate time series chart
 *
 * This component handles the specific data formatting and configuration for the indoor climate chart
 */
export const IndoorClimateChartContainer: React.FC<IndoorClimateChartContainerProps> = ({
  sensors,
  selectedSensors,
  outdoorSeries,
  flexstationTemperatureBounds,
  visibleSeries,
  onSeriesToggle,
  dateRange,
  resolution = "hourly",
  isLoading = false,
  chartHeight = 680,
  targetTemperature,
}) => {
  const { t } = useTranslation(["propai", "propai_properties", "_common"]);
  const graphColors = useGraphColors();
  const { branding } = useBrandingContext();

  // Transform sensors data
  const transformedSensors = useMemo((): TransformedSensor[] => {
    if (!sensors) return [];
    return sensors.map((sensor) => ({
      ...sensor,
      data: transformSensorsData(sensor.data ?? []),
    }));
  }, [sensors]);

  // Filter to selected sensors
  const filteredSensors = useMemo((): TransformedSensor[] => {
    if (!transformedSensors) return [];
    return transformedSensors.filter((sensor) => selectedSensors.includes(sensor.uid));
  }, [selectedSensors, transformedSensors]);

  // Calculate temperature bounds for indoor temperatures
  const indoorTemperatureAxisBounds = useMemo(() => {
    const allTempValues: number[] = [];
    filteredSensors.forEach((sensor) => {
      sensor.data?.forEach((dataPoint) => {
        if (dataPoint.temp != null && !Number.isNaN(dataPoint.temp)) {
          allTempValues.push(dataPoint.temp);
        }
      });
    });
    flexstationTemperatureBounds?.forEach((point) => {
      if (point.min != null && !Number.isNaN(point.min)) allTempValues.push(point.min);
      if (point.max != null && !Number.isNaN(point.max)) allTempValues.push(point.max);
    });
    return calculateTemperatureAxisBounds(allTempValues);
  }, [filteredSensors, flexstationTemperatureBounds]);

  // Calculate temperature bounds for outdoor temperatures
  const outdoorTemperatureAxisBounds = useMemo(() => {
    if (!outdoorSeries || outdoorSeries.length === 0) {
      return { min: -10, max: 30 };
    }
    const values = outdoorSeries
      .map((point) => point.y)
      .filter((temp) => temp != null && !Number.isNaN(temp));
    return calculateTemperatureAxisBounds(values);
  }, [outdoorSeries]);

  // Build series configuration
  const series = useMemo((): TimeSeriesData[] => {
    const seriesArr: TimeSeriesData[] = [];

    // Outdoor temperature series
    seriesArr.push({
      uid: IndoorClimateSeriesType.OUTDOOR_TEMP,
      name: t("_common:text_outdoor_temperature"),
      data: (outdoorSeries || []).map((point) => ({
        datetime: DateTime.fromMillis(point.x).toISO() ?? "",
        value: point.y,
      })),
      unit: "°C",
      color: graphColors[2],
      yAxisIndex: 0,
      visible: visibleSeries.outdoor,
    });

    // Aggregated min / max indoor temperature across all flexstation sensors.
    // Plotted on the indoor-temperature axis so they're directly comparable
    // to the average / individual sensor lines.
    if (flexstationTemperatureBounds && flexstationTemperatureBounds.length > 0) {
      const minData = flexstationTemperatureBounds
        .filter((p) => p.min != null && !Number.isNaN(p.min))
        .map((p) => ({ datetime: p.timestamp, value: p.min as number }));
      const maxData = flexstationTemperatureBounds
        .filter((p) => p.max != null && !Number.isNaN(p.max))
        .map((p) => ({ datetime: p.timestamp, value: p.max as number }));
      const rangeData = flexstationTemperatureBounds
        .filter(
          (p) => p.min != null && !Number.isNaN(p.min) && p.max != null && !Number.isNaN(p.max)
        )
        .map((p) => ({
          datetime: p.timestamp,
          low: p.min as number,
          high: p.max as number,
        }));

      seriesArr.push({
        uid: IndoorClimateSeriesType.FLEXSTATION_TEMP_RANGE,
        name: `${t("indoor_temperature")} (${t("_common:text_minimum")} – ${t("_common:text_maximum")})`,
        data: [],
        rangeData,
        unit: getColumnSuffix("temp"),
        color: branding.colors.outline,
        yAxisIndex: 1,
        type: "arearange",
        fillOpacity: 0.28,
        lineWidth: 0,
        zIndex: 0,
        enableMouseTracking: false,
        // Hide the band when either bound is off
        visible:
          visibleSeries.flex_temp_range !== false &&
          visibleSeries.flex_temp_min !== false &&
          visibleSeries.flex_temp_max !== false,
        // The band auto-couples to min/max visibility, so a separate legend
        // entry would feel unresponsive when one bound is off.
        showInLegend: false,
      });
      seriesArr.push({
        uid: IndoorClimateSeriesType.FLEXSTATION_TEMP_MIN,
        name: `${t("indoor_temperature")} (${t("_common:text_minimum")})`,
        data: minData,
        unit: getColumnSuffix("temp"),
        color: heatRangeColors.low,
        yAxisIndex: 1,
        dashStyle: "ShortDash",
        visible: visibleSeries.flex_temp_min,
      });
      seriesArr.push({
        uid: IndoorClimateSeriesType.FLEXSTATION_TEMP_MAX,
        name: `${t("indoor_temperature")} (${t("_common:text_maximum")})`,
        data: maxData,
        unit: getColumnSuffix("temp"),
        color: heatRangeColors.high,
        yAxisIndex: 1,
        dashStyle: "ShortDash",
        visible: visibleSeries.flex_temp_max,
      });
    }

    // Process sensor series
    filteredSensors.forEach((sensor) => {
      const isAverageSensor = sensor.uid === AVERAGE_SENSOR_UID;
      const sensorData = sensor.data || [];

      if (isAverageSensor) {
        // Average sensor temperature and humidity
        seriesArr.push({
          uid: IndoorClimateSeriesType.AVERAGE_TEMP,
          name: `${sensor.label} ${t("indoor_temperature")}`,
          data: sensorData.map((s) => ({
            datetime: DateTime.fromMillis(s.ts).toISO() ?? "",
            value: s.temp,
          })),
          unit: getColumnSuffix("temp"),
          color: graphColors[0],
          yAxisIndex: 1,
          visible: visibleSeries.temp,
        });
        seriesArr.push({
          uid: IndoorClimateSeriesType.AVERAGE_HUMID,
          name: `${sensor.label} ${t("indoor_humidity")}`,
          data: sensorData.map((s) => ({
            datetime: DateTime.fromMillis(s.ts).toISO() ?? "",
            value: s.humid,
          })),
          unit: getColumnSuffix("humid"),
          color: graphColors[1],
          yAxisIndex: 2,
          visible: visibleSeries.humid,
        });
        return;
      }

      // Individual sensor temperature.
      // Hidden from the native top legend and render in a seperate side panel.
      if (visibleSeries[`temp_${sensor.uid}`] !== undefined) {
        seriesArr.push({
          uid: `temp_${sensor.uid}`,
          name: `${sensor.label} ${t("temperature")}`,
          data: sensorData.map((s) => ({
            datetime: DateTime.fromMillis(s.ts).toISO() ?? "",
            value: s.temp,
          })),
          unit: getColumnSuffix("temp"),
          yAxisIndex: 1,
          visible: visibleSeries[`temp_${sensor.uid}`],
          showInLegend: false,
        });
      }

      // Individual sensor humidity.
      // Hidden from the native top legend and render in a seperate side panel.
      if (visibleSeries[`humid_${sensor.uid}`] !== undefined) {
        seriesArr.push({
          uid: `humid_${sensor.uid}`,
          name: `${sensor.label} ${t("humidity")}`,
          data: sensorData.map((s) => ({
            datetime: DateTime.fromMillis(s.ts).toISO() ?? "",
            value: s.humid,
          })),
          unit: getColumnSuffix("humid"),
          yAxisIndex: 2,
          visible: visibleSeries[`humid_${sensor.uid}`],
          showInLegend: false,
        });
      }
    });

    return seriesArr;
  }, [
    filteredSensors,
    outdoorSeries,
    flexstationTemperatureBounds,
    visibleSeries,
    graphColors,
    branding.colors.outline,
    t,
  ]);

  // Build Y-axis configuration
  const yAxisConfigs = useMemo((): YAxisConfig[] => {
    const configs: YAxisConfig[] = [
      {
        title: { text: `${t("_common:text_outdoor_temperature")} (°C)`, useHTML: true },
        min: outdoorTemperatureAxisBounds.min,
        max: outdoorTemperatureAxisBounds.max,
        gridLineWidth: 0,
      },
      {
        title: { text: `${t("indoor_temperature")} (°C)`, useHTML: true },
        min: indoorTemperatureAxisBounds.min,
        max: indoorTemperatureAxisBounds.max,
        gridLineWidth: 0,
        plotLines: targetTemperature
          ? [
              {
                value: targetTemperature,
                color: heatRangeColors.high,
                width: 1,
                zIndex: 1,
                dashStyle: "ShortDash",
                label: {
                  text: t("propai_properties:chart_target_temperature"),
                  style: {
                    color: heatRangeColors.high,
                  },
                },
              },
            ]
          : undefined,
      },
      {
        title: { text: `${t("humidity")} (%)`, useHTML: true },
        gridLineWidth: 0,
      },
    ];

    return configs;
  }, [t, indoorTemperatureAxisBounds, outdoorTemperatureAxisBounds, targetTemperature]);

  // Series owned by the right-hand "Selected sensors" panel
  const selectionSeries = useMemo(
    () => series.filter((s) => s.uid.startsWith("temp_") || s.uid.startsWith("humid_")),
    [series]
  );
  const hasSelection = selectionSeries.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        width: "100%",
        height: "100%",
        gap: 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <BaseTimeSeriesChart
          series={series}
          yAxisConfigs={yAxisConfigs}
          chartHeight={chartHeight}
          dateRange={dateRange}
          resolution={resolution}
          isLoading={isLoading}
          allowSeriesToggle={true}
          onSeriesToggle={onSeriesToggle}
          yAxisLayout="stacked"
          legendEnabled={true}
        />
      </Box>
      {hasSelection && (
        <Box
          sx={{
            width: { xs: "100%", md: 260 },
            flexShrink: 0,
            maxHeight: { xs: 200, md: chartHeight },
          }}
        >
          <IndoorClimateChartLegend
            series={selectionSeries}
            onSeriesToggle={onSeriesToggle}
            title={t("selected_sensors", { defaultValue: "Selected sensors" })}
          />
        </Box>
      )}
    </Box>
  );
};

export { IndoorClimateSeriesType } from "./IndoorClimateChartContainer.types";
